import { useEffect, useRef, useState } from "react";
import { Loader2, PlugZap, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { getMpConnect } from "@/lib/matterport";

// Self-contained live tester: embeds a Matterport model with the current SDK
// key and reports whether the Showcase SDK connects (runs in the user's browser).
export default function SdkTest({ sdkKey }) {
  const iframeRef = useRef(null);
  const [modelId, setModelId] = useState("");
  const [runId, setRunId] = useState(0);
  const [testedModel, setTestedModel] = useState("");
  const [status, setStatus] = useState("idle"); // idle | connecting | connected | error
  const [sweeps, setSweeps] = useState(null);
  const [modelName, setModelName] = useState(null);
  const [errMsg, setErrMsg] = useState("");

  const appDomain = typeof window !== "undefined" ? window.location.hostname : "";

  const run = () => {
    if (!sdkKey?.trim()) { setErrMsg("Save your SDK key first."); setStatus("error"); setTestedModel(""); setRunId(0); return; }
    if (!modelId.trim()) { setErrMsg("Enter a Model ID to test."); setStatus("error"); setTestedModel(""); setRunId(0); return; }
    setErrMsg(""); setSweeps(null); setModelName(null);
    setTestedModel(modelId.trim());
    setStatus("connecting");
    setRunId((x) => x + 1);
  };

  useEffect(() => {
    if (runId === 0) return;
    let cancelled = false;
    let timer = null;
    (async () => {
      try {
        // give the iframe a moment to mount/load before connecting
        await new Promise((r) => { timer = setTimeout(r, 800); });
        const iframe = iframeRef.current;
        if (cancelled) return;
        if (!iframe) { setStatus("error"); setErrMsg("Viewer failed to mount."); return; }
        const connect = await getMpConnect(sdkKey);
        const sdk = await connect(iframe);
        if (cancelled) return;
        setStatus("connected");
        try { const m = await sdk.Model.getData(); if (!cancelled) setModelName(m.label || m.sid || "(loaded)"); } catch (e) {}
        sdk.Sweep?.data?.subscribe({ onCollectionUpdated: (coll) => { if (!cancelled) setSweeps(Object.keys(coll || {}).length); } });
      } catch (e) {
        if (!cancelled) { console.error("[Matterport] SDK test connect failed:", e); setStatus("error"); setErrMsg(String(e?.message || e || "Connection failed")); }
      }
    })();
    return () => { cancelled = true; if (timer) clearTimeout(timer); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [runId]);

  const src = testedModel
    ? `https://my.matterport.com/show/?m=${testedModel}&play=1&qs=1&applicationKey=${sdkKey?.trim() || ""}`
    : "";

  return (
    <div className="border border-white/10 bg-[#121212] p-8 mt-6">
      <div className="flex items-center gap-2 mb-1">
        <PlugZap className="w-4 h-4 text-[#C5A24A]" />
        <h2 className="font-display font-bold tracking-tight">Test SDK Connection</h2>
      </div>
      <p className="text-sm text-muted-foreground mb-5">
        Paste any Matterport <span className="text-white">Model ID</span> to confirm your key connects. This runs in your
        browser (Matterport blocks automated ones), so test it here rather than trusting a server check.
      </p>

      <div className="flex gap-2">
        <Input
          data-testid="sdk-test-model-input"
          value={modelId}
          onChange={(e) => setModelId(e.target.value)}
          placeholder="e.g. SxQL3iGyoDo"
          aria-label="Matterport Model ID to test"
          className="bg-[#0A0A0A] border-white/10 font-mono text-sm h-11"
        />
        <Button data-testid="sdk-test-run" onClick={run} className="bg-[#C5A24A] hover:bg-[#A8863A] text-white h-11 shrink-0">
          Test connection
        </Button>
      </div>

      {(runId > 0 || status === "error") && (
        <div className="mt-5 grid md:grid-cols-[1fr_260px] gap-4">
          <div className="relative h-64 bg-[#0A0A0A] border border-white/10 rounded overflow-hidden">
            {testedModel ? (
              <iframe
                key={runId}
                ref={iframeRef}
                data-testid="sdk-test-iframe"
                title="Matterport SDK test"
                src={src}
                className="w-full h-full border-0"
                allow="xr-spatial-tracking; fullscreen"
                allowFullScreen
              />
            ) : (
              <div className="absolute inset-0 flex items-center justify-center text-sm text-muted-foreground">No model loaded</div>
            )}
          </div>
          <div className="text-sm space-y-3" data-testid="sdk-test-panel">
            <div className="flex items-center gap-2" data-testid="sdk-test-status">
              {status === "connecting" && <><Loader2 className="w-4 h-4 animate-spin text-[#FFCC00]" /> <span className="text-[#FFCC00]">Connecting…</span></>}
              {status === "connected" && <><CheckCircle2 className="w-4 h-4 text-[#34C759]" /> <span className="text-[#34C759] font-semibold">SDK connected</span></>}
              {status === "error" && <><XCircle className="w-4 h-4 text-[#FF3B30]" /> <span className="text-[#FF3B30] font-semibold">Not connected</span></>}
            </div>
            <div><span className="text-muted-foreground">Sweeps found:</span> {sweeps ?? "—"}</div>
            <div><span className="text-muted-foreground">Model:</span> {modelName ?? "—"}</div>
            {errMsg && <div className="text-xs text-[#FF3B30]">{errMsg}</div>}
            {status === "error" && (
              <div className="text-xs text-muted-foreground border-t border-white/10 pt-3">
                If the space says "not found": the Model ID may be wrong, or this app's domain isn't on your SDK key's
                allowlist. In Matterport → Settings → Developer Tools → your SDK Key, add:
                <code className="block mt-1 text-[#D8BE86] break-all">{appDomain}</code>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
